import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Grid, Card, Icon, Skeleton } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { getInventoryKPI } from "api/inventorySpareparts";
import Loading from "components/Loading";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import { getInventoryUsageTrend } from "api/inventorySpareparts";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import TopUsedParts from "./TopUsedParts";
import useGlobalStore from "store/useGlobalStore";

// Skeleton loading component for statistics cards
const StatisticsCardSkeleton = () => (
    <Card sx={{ p: 2 }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDBox>
                <Skeleton variant="text" width={120} height={20} />
                <Skeleton variant="text" width={80} height={32} />
                <Skeleton variant="text" width={100} height={16} />
            </MDBox>
            <Skeleton variant="circular" width={40} height={40} />
        </MDBox>
    </Card>
);

// Skeleton loading for chart component
const ChartSkeleton = () => (
    <Card sx={{ p: 2 }}>
        <MDBox mb={2}>
            <Skeleton variant="text" width={200} height={24} />
            <Skeleton variant="text" width={150} height={16} />
        </MDBox>
        <Skeleton variant="rectangular" width="100%" height={300} />
    </Card>
);

// Memoized InventoryUsageTrend Component to prevent unnecessary re-renders
const InventoryUsageTrend = React.memo(() => {
    const [usageData, setUsageData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { showKPI } = useGlobalStore()

    useEffect(() => {
        if (showKPI && !usageData)
        fetchUsageTrend();
    }, [showKPI]);

    const fetchUsageTrend = async () => {
        try {
            setIsLoading(true);
            const response = await getInventoryUsageTrend();
            setUsageData(response.data);
        } catch (error) {
            console.error("Failed to fetch usage trend:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Memoize the chart data transformation
    const chartData = useMemo(() => {
        if (!usageData || !usageData.data) {
            return {
                labels: [],
                datasets: {
                    label: "Daily Usage",
                    data: []
                }
            };
        }

        const labels = usageData.data.map(item => {
            const date = new Date(item.day);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });

        const data = usageData.data.map(item => item.total_used);

        return {
            labels,
            datasets: {
                label: "Parts Used",
                data
            }
        };
    }, [usageData]);

    if (isLoading) {
        return <ChartSkeleton />;
    }

    return (
        <MDBox mt={4}>
            <ReportsBarChart
                color="info"
                title="Inventory Usage Trend"
                description={`Daily spare parts consumption over ${usageData?.days || 30} days`}
                date={`From ${usageData?.from} to ${usageData?.to}`}
                chart={chartData}
            />
        </MDBox>
    );
});

// Main Inventory KPI Cards Component
const InventoryKPICards = () => {
    const [kpiData, setKpiData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { showKPI } = useGlobalStore()

    useEffect(() => {
        if (showKPI && !kpiData)
            fetchKPIData();
    }, [showKPI]);

    const fetchKPIData = async () => {
        try {
            setIsLoading(true);
            const response = await getInventoryKPI();
            setKpiData(response.data);
        } catch (error) {
            console.error("Failed to fetch KPI data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Memoize currency formatting function
    const formatCurrency = useMemo(() => {
        return (value) => {
            return new Intl.NumberFormat('en-MY', {
                style: 'currency',
                currency: 'MYR',
                minimumFractionDigits: 2,
            }).format(value);
        };
    }, []);

    // Memoize formatted values to prevent recalculation
    const memoizedValues = useMemo(() => ({
        totalSpareparts: isLoading ? "-" : kpiData?.total_spareparts || 0,
        belowMinimum: isLoading ? "-" : kpiData?.below_minimum_count || 0,
        outOfStock: isLoading ? "-" : kpiData?.out_of_stock_count || 0,
        reorderCandidates: isLoading ? "-" : kpiData?.reorder_candidates_count || 0,
        totalValue: isLoading ? "-" : formatCurrency(kpiData?.total_inventory_value || 0),
        usage30Days: isLoading ? "-" : kpiData?.usage_last_30_days || 0,
        topUsedParts: kpiData?.top_used_parts || []
    }), [isLoading, kpiData, formatCurrency]);

    if(!showKPI) {
        return null; // Don't render anything if KPI is hidden and data not loaded
    }

    return (
        <MDBox mt={4} mb={4}>
            <Grid container spacing={3}>
                {/* First Row - Main KPIs */}
                <Grid item xs={12} sm={6} md={4}>
                    <MDBox mb={1.5}>
                        {isLoading ? (
                            <StatisticsCardSkeleton />
                        ) : (
                            <ComplexStatisticsCard
                                icon="inventory_2"
                                title="Total Spare Parts"
                                color="primary"
                                count={memoizedValues.totalSpareparts}
                            />
                        )}
                    </MDBox>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    {isLoading ? (
                        <StatisticsCardSkeleton />
                    ) : (
                        <ComplexStatisticsCard
                            icon="warning"
                            color="warning"
                            title="Below Minimum"
                            count={memoizedValues.belowMinimum}
                            subtitle="Items need reorder"
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    {isLoading ? (
                        <StatisticsCardSkeleton />
                    ) : (
                        <ComplexStatisticsCard
                            icon="error"
                            title="Out of Stock"
                            color="error"
                            count={memoizedValues.outOfStock}
                            subtitle="Critical items"
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    {isLoading ? (
                        <StatisticsCardSkeleton />
                    ) : (
                        <ComplexStatisticsCard
                            icon="shopping_cart"
                            title="Reorder Candidates"
                            color="info"
                            count={memoizedValues.reorderCandidates}
                            subtitle="Action required"
                        />
                    )}
                </Grid>

                {/* Second Row - Financial and Usage */}
                <Grid item xs={12} sm={6} md={4}>
                    {isLoading ? (
                        <StatisticsCardSkeleton />
                    ) : (
                        <ComplexStatisticsCard
                            icon="attach_money"
                            title="Total Inventory Value"
                            color="success"
                            count={memoizedValues.totalValue}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    {isLoading ? (
                        <StatisticsCardSkeleton />
                    ) : (
                        <ComplexStatisticsCard
                            icon="timeline"
                            title="Usage (last 30 days)"
                            color="secondary"
                            count={memoizedValues.usage30Days}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TopUsedParts
                        data={memoizedValues.topUsedParts}
                        isLoading={isLoading}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <InventoryUsageTrend />
                </Grid>
            </Grid>
        </MDBox >
    );
};

export default InventoryKPICards;
